import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { Lock, Eye, EyeOff, ArrowLeft, CheckCircle } from 'lucide-react';
import { toast } from 'react-toastify';
import api from '../services/api';

const PasswordInput = ({ id, placeholder, value, onChange, required }) => {
  const [show, setShow] = useState(false);
  return (
    <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
      <Lock size={16} style={{ position: 'absolute', left: '14px', color: '#94a3b8', pointerEvents: 'none' }} />
      <input
        id={id}
        type={show ? 'text' : 'password'}
        required={required}
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        style={{
          width: '100%',
          padding: '11px 44px 11px 40px',
          border: '1px solid #e2e8f0',
          borderRadius: '8px',
          fontSize: '14px',
          color: '#1e293b',
          background: '#f8fafc',
          outline: 'none',
          boxSizing: 'border-box',
          transition: 'border-color 0.2s, box-shadow 0.2s',
        }}
        onFocus={e => {
          e.target.style.borderColor = '#3b82f6';
          e.target.style.boxShadow = '0 0 0 3px rgba(59,130,246,0.12)';
          e.target.style.background = '#ffffff';
        }}
        onBlur={e => {
          e.target.style.borderColor = '#e2e8f0';
          e.target.style.boxShadow = 'none';
          e.target.style.background = '#f8fafc';
        }}
      />
      <button
        type="button"
        onClick={() => setShow(s => !s)}
        style={{
          position: 'absolute', right: '12px',
          background: 'none', border: 'none', cursor: 'pointer',
          padding: '2px', display: 'flex', color: '#94a3b8',
        }}
      >
        {show ? <EyeOff size={16} /> : <Eye size={16} />}
      </button>
    </div>
  );
};

const strengthLabel = (pwd) => {
  if (!pwd) return null;
  if (pwd.length < 6) return { label: 'Muito curta', color: '#ef4444', width: '25%' };
  if (pwd.length < 8) return { label: 'Fraca', color: '#f59e0b', width: '50%' };
  if (/[A-Z]/.test(pwd) && /[0-9]/.test(pwd)) return { label: 'Forte', color: '#10b981', width: '100%' };
  return { label: 'Média', color: '#3b82f6', width: '75%' };
};

const ResetPassword = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const token = params.get('token') || '';
  const strength = strengthLabel(password);

  useEffect(() => {
    if (!token) toast.error('Link inválido ou expirado');
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 6) { toast.error('A senha deve ter pelo menos 6 caracteres'); return; }
    if (password !== confirm) { toast.error('As senhas não coincidem'); return; }
    setLoading(true);
    try {
      await api.post('/auth/password/reset', { token, password });
      setDone(true);
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Falha ao redefinir senha');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: '#f1f5f9',
      padding: '24px',
    }}>
      <div style={{ width: '100%', maxWidth: '420px' }}>

        {/* Logo */}
        <div style={{ textAlign: 'center', marginBottom: '32px' }}>
          <div style={{
            width: '64px', height: '64px', borderRadius: '50%',
            background: 'linear-gradient(135deg, #3b82f6, #1d4ed8)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            margin: '0 auto 14px',
            boxShadow: '0 4px 20px rgba(59,130,246,0.3)',
          }}>
            <img src="/apas.png" alt="Apascentar" style={{ width: '46px', height: '46px', borderRadius: '50%', objectFit: 'cover' }} />
          </div>
          <p style={{ fontSize: '13px', color: '#64748b', margin: 0 }}>Igreja Apascentar</p>
        </div>

        {/* Card */}
        <div style={{
          background: '#ffffff',
          borderRadius: '16px',
          padding: '36px 32px',
          boxShadow: '0 4px 24px rgba(0,0,0,0.08)',
          border: '1px solid #f1f5f9',
        }}>
          {!done ? (
            <>
              <div style={{ marginBottom: '28px' }}>
                <h2 style={{ fontSize: '20px', fontWeight: '700', color: '#1e293b', margin: '0 0 8px' }}>
                  Redefinir senha
                </h2>
                <p style={{ fontSize: '13px', color: '#64748b', margin: 0 }}>
                  Escolha uma nova senha segura para sua conta.
                </p>
              </div>

              {!token ? (
                <div style={{
                  padding: '16px', background: '#fef2f2', borderRadius: '8px',
                  border: '1px solid #fecaca', color: '#dc2626', fontSize: '14px', textAlign: 'center',
                }}>
                  Link inválido ou expirado. Solicite um novo link.
                </div>
              ) : (
                <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: '18px' }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', color: '#374151', marginBottom: '6px' }}>
                      Nova senha
                    </label>
                    <PasswordInput
                      id="password"
                      placeholder="Mínimo 6 caracteres"
                      value={password}
                      onChange={e => setPassword(e.target.value)}
                      required
                    />
                    {strength && (
                      <div style={{ marginTop: '8px' }}>
                        <div style={{ height: '4px', background: '#e2e8f0', borderRadius: '4px', overflow: 'hidden' }}>
                          <div style={{ height: '100%', width: strength.width, background: strength.color, borderRadius: '4px', transition: 'width 0.3s, background 0.3s' }} />
                        </div>
                        <p style={{ fontSize: '11px', color: strength.color, margin: '4px 0 0', fontWeight: '500' }}>
                          {strength.label}
                        </p>
                      </div>
                    )}
                  </div>

                  <div>
                    <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', color: '#374151', marginBottom: '6px' }}>
                      Confirmar senha
                    </label>
                    <PasswordInput
                      id="confirm"
                      placeholder="Repita a nova senha"
                      value={confirm}
                      onChange={e => setConfirm(e.target.value)}
                      required
                    />
                    {confirm && password !== confirm && (
                      <p style={{ fontSize: '11px', color: '#ef4444', margin: '4px 0 0' }}>As senhas não coincidem</p>
                    )}
                    {confirm && password === confirm && confirm.length >= 6 && (
                      <p style={{ fontSize: '11px', color: '#10b981', margin: '4px 0 0' }}>Senhas coincidem</p>
                    )}
                  </div>

                  <button
                    type="submit"
                    disabled={loading || !token}
                    style={{
                      width: '100%',
                      padding: '12px',
                      background: loading ? '#93c5fd' : 'linear-gradient(135deg, #3b82f6, #2563eb)',
                      color: '#ffffff',
                      border: 'none',
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontWeight: '600',
                      cursor: loading ? 'not-allowed' : 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px',
                      boxShadow: loading ? 'none' : '0 4px 12px rgba(59,130,246,0.35)',
                      transition: 'opacity 0.2s',
                      marginTop: '4px',
                    }}
                    onMouseOver={e => { if (!loading) e.currentTarget.style.opacity = '0.92'; }}
                    onMouseOut={e => { e.currentTarget.style.opacity = '1'; }}
                  >
                    {loading ? (
                      <>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ animation: 'spin 0.8s linear infinite' }}>
                          <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
                        </svg>
                        Redefinindo...
                      </>
                    ) : 'Redefinir senha'}
                  </button>

                  <p style={{ fontSize: '12px', color: '#94a3b8', textAlign: 'center', margin: 0 }}>
                    O link expira em 24 horas após o envio.
                  </p>
                </form>
              )}
            </>
          ) : (
            /* Estado de sucesso */
            <div style={{ textAlign: 'center', padding: '8px 0' }}>
              <div style={{
                width: '64px', height: '64px', borderRadius: '50%',
                background: '#f0fdf4',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                margin: '0 auto 20px',
              }}>
                <CheckCircle size={32} color="#10b981" />
              </div>
              <h2 style={{ fontSize: '20px', fontWeight: '700', color: '#1e293b', margin: '0 0 10px' }}>
                Senha redefinida!
              </h2>
              <p style={{ fontSize: '14px', color: '#64748b', lineHeight: '1.6', margin: '0 0 28px' }}>
                Sua senha foi atualizada com sucesso. Agora você pode entrar com a nova senha.
              </p>
              <button
                onClick={() => navigate('/login')}
                style={{
                  width: '100%',
                  padding: '12px',
                  background: 'linear-gradient(135deg, #3b82f6, #2563eb)',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  boxShadow: '0 4px 12px rgba(59,130,246,0.35)',
                }}
              >
                Ir para o login
              </button>
            </div>
          )}

          {/* Voltar ao login */}
          {!done && (
            <div style={{ marginTop: '28px', paddingTop: '20px', borderTop: '1px solid #f1f5f9', textAlign: 'center' }}>
              <Link
                to="/login"
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: '6px',
                  fontSize: '13px', color: '#64748b', textDecoration: 'none', fontWeight: '500',
                }}
                onMouseOver={e => e.currentTarget.style.color = '#3b82f6'}
                onMouseOut={e => e.currentTarget.style.color = '#64748b'}
              >
                <ArrowLeft size={14} />
                Voltar ao login
              </Link>
            </div>
          )}
        </div>

        <p style={{ textAlign: 'center', fontSize: '12px', color: '#94a3b8', marginTop: '24px' }}>
          © {new Date().getFullYear()} Igreja Apascentar Curitiba
        </p>
      </div>

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

export default ResetPassword;
